// --- Перечисления -----------------------------------------------------------

export type Role = "client" | "manager" | "owner" | "admin";
export type Category =
  | "tshirt" | "polo" | "longsleeve"
  | "hoodie" | "olimp" | "fleece" | "sweater" | "vest" | "jersey"
  | "suit" | "shirt"
  | "jeans" | "pants" | "shorts" | "sweatpants"
  | "blazer" | "jacket" | "coat" | "puffer"
  | "shoes" | "hat" | "accessory";
export type Condition = "excellent" | "good" | "fair";
export type ProductStatus = "available" | "on_hold" | "sold";
export type OrderType = "purchase" | "reservation";
export type ReservationType = "free" | "week" | "month";
export type OrderStatus = "pending" | "issued" | "expired" | "cancelled";
export type DepositStatus = "none" | "paid" | "credited" | "burned";

// --- Интерфейсы -------------------------------------------------------------

export type Gender = "male" | "female";

export interface User {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  role: Role;
  gender: Gender | null;
  height: number | null;
  shoe_size: string | null;
  clothing_size: string | null;
}

export interface Product {
  id: number;
  sku: string;
  file_id: string;
  extra_file_ids: string[];
  title: string | null;
  category: string; // код пресета («jeans») или произвольная категория («Ремни»)
  size: string;
  price: number;
  condition: Condition;
  status: ProductStatus;
  created_at: string;
  blur?: string | null; // LQIP data-URI для мгновенной заглушки
  views?: number;       // просмотры карточки
  likes?: number;       // число лайков (соцдоказательство)
}

export interface Receipt {
  order_id: number;
  kind: "purchase" | "deposit";
  method: "online" | "cash" | "transfer" | "unknown";   // чем заплатили
  sku: string;
  title: string | null;
  size: string;
  deposit_credited: number;       // залог, зачтённый в стоимость покупки
  amount: number;
  currency: string;
  charge_id: string;
  paid_at: string;
  demo: boolean;
}

export interface ProductBrief {
  id: number;
  sku: string;
  file_id: string;
  title: string | null;
  category: string; // код пресета («jeans») или произвольная категория («Ремни»)
  size: string;
  price: number;
}

export interface ClientBrief {
  id: number;
  username: string | null;
  first_name: string | null;
  phone_last4: string | null;
}

export interface Order {
  id: number;
  product_id: number;
  client_id: number;
  manager_id: number | null;
  type: OrderType;
  reservation_type: ReservationType | null;
  amount: number;
  deposit_amount: number;
  deposit_status: DepositStatus;
  status: OrderStatus;
  created_at: string;
  expires_at: string | null;
  issued_at: string | null;
  paid_at?: string | null;
  payment_method?: "online" | "cash" | "transfer" | null;
  payment_charge_id?: string | null;
  product: ProductBrief;
  client?: ClientBrief;
  manager?: ClientBrief | null;
}

export interface Shift {
  id: number;
  manager_id: number;
  opened_at: string;
  closed_at: string | null; // null = смена открыта
  auto_closed: boolean;
  sales_count: number;
  revenue: number;
  reservations_count: number;
  reservations_burned: number;
  deposits_burned_amount: number;
}

export interface Subscription {
  id: number;
  category: Category | null;
  size: string | null;
  price_max: number | null;
  created_at: string;
}

// Сводка каталога для главной витрины (GET /products/stats).
export interface CategoryStat {
  category: string;
  count: number;
  cover_id: number;   // ID товара для обложки плитки
}

export interface CatalogStats {
  available: number;  // всего в наличии
  new_week: number;   // добавлено за последние 7 дней
  my_size: number;    // подходит под размеры из профиля (0, если профиль пуст)
  categories: CategoryStat[];
}

export interface PriceChange {
  id: number;
  product: ProductBrief;
  changed_by_name: string | null;
  changed_by_username: string | null;
  old_price: number;
  new_price: number;
  changed_at: string;
}

// --- Локализация ------------------------------------------------------------

export const CATEGORY_LABELS: Record<Category, string> = {
  tshirt:     "Футболки",
  polo:       "Поло",
  longsleeve: "Лонгсливы",
  hoodie:     "Худи",
  olimp:      "Олимпийки",
  fleece:     "Флисы",
  sweater:    "Свитеры",
  vest:       "Жилеты",
  jersey:     "Джерси",
  suit:       "Костюмы",
  shirt:      "Рубашки",
  jeans:      "Джинсы",
  pants:      "Брюки",
  shorts:     "Шорты",
  sweatpants: "Штаны",
  blazer:     "Пиджаки",
  jacket:     "Куртки",
  coat:       "Пальто",
  puffer:     "Пуховики",
  shoes:      "Обувь",
  hat:        "Головные уборы",
  accessory:  "Аксессуары",
};

// Состояние вещи. Винтаж продаётся «как есть», и три служебных кода ничего не
// говорят покупателю — поэтому у каждого есть и короткое имя, и расшифровка.
export const CONDITION_LABELS: Record<Condition, { label: string; hint: string }> = {
  excellent: { label: "Отличное",          hint: "без следов носки" },
  good:      { label: "Хорошее",           hint: "лёгкие следы носки, без дефектов" },
  fair:      { label: "Винтажное",         hint: "заметные следы времени — часть характера вещи" },
};

// Подпись категории: для пресета — из словаря, для произвольной — сам текст как есть.
export function categoryLabel(c: string): string {
  return (CATEGORY_LABELS as Record<string, string>)[c] ?? c;
}
